
// --- Vue & Template imports ---
import { Component, Prop, Vue } from 'vue-property-decorator';

// --- Models ---
import { User } from '@/models/User.model';

// --- Services ---
import AppService from '@/services/app';
import AuthService from '@/services/auth';
import UserService from '@/services/user';

// --- Third Party imports ---

@Component
export default class UserListing extends Vue {

  // used by Filter Area
  search: string = '';

  // used by No Data Slot
  isLoading: boolean = true;

  // used by Item Dialog for Create/Update
  showItemDialog: boolean = false;
  title: string = '';
  clickedItem: User = {
    id: 0,
    userTypeId: 0,
    userType: '',
    clearanceLevel: 0,
    username: '',
    firstName: '',
    lastName: '',
    email: '',
    isLoggedInUser: false,
  };
  password: string = '';
  showPassword: boolean = true;

  isUpdate: boolean = false;
  valid: boolean = false;

  rules = {
    required: (value: string) => !!value || '',
    min: (v: string) => v.length >= 8 || 'Min 8 characters',
    name: (value: string) => /^(?=.*[a-zA-Z- ])[a-zA-Z- ]+$/.test(value) || 'Remove invalid characters',
    optionalEmail: (value: string) => (value != null && value.length > 0) ? /(?:[a-zA-Z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-zA-Z0-9!#$%&'*+/=?^_`{|}~-]+)*|"(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21\x23-\x5b\x5d-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])*")@(?:(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?|\[(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?|[a-z0-9-]*[a-z0-9]:(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21-\x5a\x53-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])+)\])/.test(value) || 'E-mail must be valid' : true,
    username: (value: string) => /^(?=.*[a-zA-Z0-9- _.@])[a-zA-Z0-9- _.@]+$/.test(value) || 'Remove invalid characters',
  };

  clicked: boolean = false;

  showResetPasswordDialog: boolean = false;
  validPassword: boolean = false;

  // used by Confirm Action Dialog for Delete
  action: string = '';
  dialogContent: string = '';
  showConfirmActionDialog: boolean = false;
  confirmActionDelay: boolean = false;

  // #region Functions to display list data on page load
    headers() {
      return [
        {
          sortable: false,
          text: '',
          value: 'loggedInUserIndicator',
          align: 'end',
        },
        {
          sortable: false,
          text: '',
          value: 'avatar',
        },
        {
          text: 'Name',
          value: 'name',
        }, 
        {
          text: 'Username',
          value: 'username',
        },
        {
          text: 'User Type',
          value: 'userType',
        },
        {
          text: 'Clearance',
          value: 'clearanceLevel',
          align: 'start',
        },               
        {
          sortable: false,
          text: '',
          value: 'action',
          align: 'end',
        },
      ];
    }

    customFilter(column: string, search: string, item: User) {
      let result: boolean = false;
      
      if (search === '') {
        result = true;
      } else {
        result = item.firstName.toLowerCase().includes(search.toLowerCase()) ||
          item.lastName.toLowerCase().includes(search.toLowerCase()) ||
          item.username.toLowerCase().includes(search.toLowerCase()) ||
          item.userType.toLowerCase().includes(search.toLowerCase()) ||
          ('' + item.clearanceLevel).includes(search);
      }

      return result;
    }

    customSort(items: any[], sortBy: string[], sortDesc: boolean[], locale: string) {
      // If the result is negative, a is sorted before b.
      // If the result is positive, b is sorted before a.
      // If the result is 0, no changes are done with the sort order of the two values.

      switch (sortBy[0]) {
        case 'name':
          // firstName + ' ' + lastName
          if (sortDesc[0]) {
            items.sort((a: User, b: User) => 
              (a.firstName + ' ' + a.lastName).localeCompare(b.firstName + ' ' + b.lastName));
          } else {
            items.sort((a: User, b: User) => 
              (b.firstName + ' ' + b.lastName).localeCompare(a.firstName + ' ' + a.lastName));
          }
          break;
          
        case 'username':
          if (sortDesc[0]) {
            items.sort((a: User, b: User) => a.username.toLowerCase().localeCompare(b.username.toLowerCase()));
          } else {
            items.sort((a: User, b: User) => b.username.toLowerCase().localeCompare(a.username.toLowerCase()));
          }
          break;

        case 'userType':
          if (sortDesc[0]) {
            items.sort((a: User, b: User) => a.userType.toLowerCase().localeCompare(b.userType.toLowerCase()));
          } else {
            items.sort((a: User, b: User) => b.userType.toLowerCase().localeCompare(a.userType.toLowerCase()));
          }
          break;

        case 'clearanceLevel':
          // clearanceLevel is a number
          if (sortDesc[0]) {
            items.sort((a: User, b: User) => {
              let sort = 0;

              if ((a.clearanceLevel ? a.clearanceLevel : 0) > (b.clearanceLevel ? b.clearanceLevel : 0)) {
                sort = -1;
              } else if ((b.clearanceLevel ? b.clearanceLevel : 0) > (a.clearanceLevel ? a.clearanceLevel : 0)) {
                sort = 1;
              }
              return sort;
            });
          } else {
            items.sort((a: User, b: User) => {
              let sort = 0;

              if ((a.clearanceLevel ? a.clearanceLevel : 0) > (b.clearanceLevel ? b.clearanceLevel : 0)) {
                sort = 1;
              } else if ((b.clearanceLevel ? b.clearanceLevel : 0) > (a.clearanceLevel ? a.clearanceLevel : 0)) {
                sort = -1;
              }
              return sort;
            });
          }
          break;
      }

      return items;
    }

    async beforeMount() {
      await UserService.listTypes(false);
      this.listAll();
    }

    async listAll() {
      try {
        this.isLoading = true;
        const response: string = await UserService.listAll(true);

        if (response.length > 0 && response !== 'No Data Found') {
          AppService.errorHandler(response);
        }

      } finally {
        this.isLoading = false;
      }
    }

    allowAddNewFunctionality() {
      return this.$store.getters['app/allowAccess'](this.$store.getters['users/loggedInUser'], [
        { type: 'Super User', level: 2},
        { type: 'Operator', level: 3},
      ]);
    }

    allowEditFunctionality() {
      return this.$store.getters['app/allowAccess'](this.$store.getters['users/loggedInUser'], [
        { type: 'Super User', level: 2},
        { type: 'Operator', level: 3},
      ]);
    }

    allowResetPasswordFunctionality() {
      return this.$store.getters['app/allowAccess'](this.$store.getters['users/loggedInUser'], [
        { type: 'Super User', level: 5},
        { type: 'Operator', level: 3},
      ]);
    }

    allowDeleteFunctionality() {
      return this.$store.getters['app/allowAccess'](this.$store.getters['users/loggedInUser'], [
        { type: 'Super User', level: 2},
        { type: 'Operator', level: 3},
      ]);
    }
  // #endregion

  // #region Functions required for the Item Dialog for Create/Update/View
    launchNewItemDialog() {
      this.clickedItem = {
        id: 0,
        userTypeId: 0,
        userType: '',
        clearanceLevel: 0,
        username: '',
        firstName: '',
        lastName: '',
        email: '',
        isLoggedInUser: false,
      };

      this.isUpdate = false;
      this.password = ''; // reset password to blank for a new user
      this.showPassword = false; // reset password to masked for a new user
      this.valid = false;
      this.title = 'Create New User';

      if (this.$refs.itemForm) {
        (this.$refs.itemForm as any).resetValidation();
      }

      this.showItemDialog = true;
    }

    launchUpdateItemDialog(item: User) {
      this.clickedItem = Object.assign({}, item);

      this.isUpdate = true;
      this.title = 'Update User';

      if (this.$refs.itemForm) {
        (this.$refs.itemForm as any).resetValidation();
      }

      this.showItemDialog = true;
    }

    doCreateUpdate() {
      if (this.clickedItem.id <= 0) {
        this.doCreate();
      } else {
        this.doUpdate();
      }
    }

    async doCreate() {
      try {
        this.clicked = true;

        const response = await UserService.create( {
          userTypeId: this.clickedItem.userTypeId,
          clearanceLevel: this.clickedItem.clearanceLevel,
          username: this.clickedItem.username,
          password: this.password,
          firstName: this.clickedItem.firstName,
          lastName: this.clickedItem.lastName,
          email: this.clickedItem.email, 
        });

        if (response && response.data) {
          if (response.data.result && response.data.result === 'false') {
            AppService.errorHandler(response.data.message);
          } else {
            AppService.successHandler(response.data.message);
            await this.listAll();
            this.showItemDialog = false;
          }
        } else {
          // response is undefined or has no data field - SHOULD NEVER HAPPEN!
          throw new Error('response: ' + JSON.stringify(response));
        }

      } catch (error) {

        if (error.response) {
          AppService.errorHandler(error.response.statusText);
        } else {
          AppService.logSupportDebug('Users.vue - doCreate - 796 - ' + error);
          AppService.errorHandler(this.$store.getters['app/messages'].couldNotConnect);
        }

      } finally {
        this.clicked = false;
      }
    }

    async doUpdate() {
      try {
        this.clicked = true;

        const response = await UserService.update({ 
          id: this.clickedItem.id,
          userTypeId: this.clickedItem.userTypeId,
          clearanceLevel: this.clickedItem.clearanceLevel,
          firstName: this.clickedItem.firstName,
          lastName: this.clickedItem.lastName,
          email: this.clickedItem.email,
        });

        if (response && response.data) {
          if (response.data.result && response.data.result === 'false') {
            AppService.errorHandler(response.data.message);
          } else {
            AppService.successHandler(response.data.message);
            await AuthService.checkAuth(); // will reload the loggedInUser to refresh the displayName, in case they updated themselves
            await this.listAll();
            this.showItemDialog = false;
          }
        } else {
          // response is undefined or has no data field - SHOULD NEVER HAPPEN!
          throw new Error('response: ' + JSON.stringify(response));
        }

      } catch (error) {

        if (error.response) {
          AppService.errorHandler(error.response.statusText);
        } else {
          AppService.logSupportDebug('Users.vue - doUpdate - 837 - ' + error);
          AppService.errorHandler(this.$store.getters['app/messages'].couldNotConnect);
        }

      } finally {
        this.clicked = false;
      }
    }
  // #endregion

  // region Functions required for the Reset Password Dialog
    launchResetPasswordItemDialog(item: User) {
      this.clickedItem = Object.assign({}, item);

      this.password = ''; // reset password to blank on update
      this.showPassword = false; // reset password to masked on update

      this.title = 'Reset Password for ' + item.firstName + ' ' + item.lastName;      

      if (this.$refs.resetPasswordForm) {
        (this.$refs.resetPasswordForm as any).resetValidation();
      }

      this.showResetPasswordDialog = true;
    }

    async doReset() {
      try {
        this.clicked = true;

        const response = await UserService.resetPassword({ 
          id: this.clickedItem.id,
          password: this.password,
        });

        if (response && response.data) {
          if (response.data.result && response.data.result === 'false') {
            AppService.errorHandler(response.data.message);
          } else {
            AppService.successHandler(response.data.message);
            if (this.clickedItem.isLoggedInUser) {
              await AuthService.logout();
            } else {
              await this.listAll();
              this.showResetPasswordDialog = false;
            }
          }
        } else {
          // response is undefined or has no data field - SHOULD NEVER HAPPEN!
          throw new Error('response: ' + JSON.stringify(response));
        }

      } catch (error) {

        if (error.response) {
          AppService.errorHandler(error.response.statusText);
        } else {
          AppService.logSupportDebug('Users.vue - doReset - 894 - ' + error);
          AppService.errorHandler(this.$store.getters['app/messages'].couldNotConnect);
        }
      } finally {
        this.clicked = false;
      }
    }

  // #endregion

  // #region Functions required for the Confirm Action Dialog for Delete
    launchDeleteItemDialog(item: User) {
      this.clickedItem = item;

      this.action = 'DELETE';
      this.dialogContent = 'You are about to <span style="font-weight: 700;color: #C01212">delete</span> '
      /*
        id	type
        1	  Super User
        2	  Accounting User
        3	  Administrator
        4	  Operator
        5	  Tracking User
        6   Workshop User
      */
        + ( item.userTypeId > 5 || item.userTypeId === 1 ? 'a ' : 'an ') + '<span style="font-weight: 700">' + item.userType 
        + '</span> named <span style="font-weight: 700">' + item.firstName + ' ' + item.lastName + '</span>'
        + '.<br/><br/> Are you sure?';

      this.showConfirmActionDialog = true;
    }

    ConfirmActionDialog_cancel() {
      this.showConfirmActionDialog = false;
    }

    async ConfirmActionDialog_doConfirmed() {
      if (this.action === 'DELETE') {
        await this.doDelete();
      }
    }

    async doDelete() {
      try {
        this.confirmActionDelay = true;
        const response = await UserService.delete({id: this.clickedItem.id});

        if (response && response.data) {
          if (response.data.result && response.data.result === 'false') {
            AppService.errorHandler(response.data.message);
          } else {
            AppService.successHandler(response.data.message);
            await this.listAll();
            this.showConfirmActionDialog = false;
          }
        } else {
          // response is undefined or has no data field - SHOULD NEVER HAPPEN!
          throw new Error('response: ' + JSON.stringify(response));
        }


      } catch (error) {

        if (error.response) {
          AppService.errorHandler(error.response.statusText);
        } else {
          AppService.logSupportDebug('Users.vue - doDelete - 960 - ' + error);
          AppService.errorHandler(this.$store.getters['app/messages'].couldNotConnect);
        }

      } finally {
        this.confirmActionDelay = false;        
      }
    }
  // #endregion
}
